import { apiRequest } from './client';
import {
  DuplicateCleanupResult,
  GanttValidationResult,
  ItemChangeRecord,
  MaintenanceStatusResponse,
  PipeSyncStatusResponse,
  ProcoreRemoteProject,
  QueueCountsResponse,
  QueueStatusResponse,
  ServiceCatalogEntry,
  SmartsheetContact,
  SmartsheetRemoteColumn,
  SmartsheetRemoteSheet,
  SmartsheetSheetSummary,
  SmartsheetWebhookInfo,
  SyncDirection,
  SyncedItemHistoryRecord,
  SyncItemMapping,
  SyncSession,
  SyncSessionDetailResponse,
  SyncSessionSummaryResponse,
  SystemSettingRecord,
  TriggerSyncJobResponse,
  UUID,
} from './types';

export const syncApi = {
  // --------------------------------------------------------------------------
  // Pipe Sync Status & Directional Sync Triggers (`/ms-sync/sync/*`)
  // --------------------------------------------------------------------------
  getPipeSyncStatus(pipeId: UUID): Promise<PipeSyncStatusResponse> {
    return apiRequest<PipeSyncStatusResponse>(`/ms-sync/sync/${pipeId}/status`);
  },

  triggerProcoreToSmartsheet(pipeId: UUID): Promise<TriggerSyncJobResponse> {
    return apiRequest<TriggerSyncJobResponse>(
      `/ms-sync/sync/${pipeId}/procore-to-smartsheet`,
      { method: 'POST' },
    );
  },

  triggerSmartsheetToProcore(pipeId: UUID): Promise<TriggerSyncJobResponse> {
    return apiRequest<TriggerSyncJobResponse>(
      `/ms-sync/sync/${pipeId}/smartsheet-to-procore`,
      { method: 'POST' },
    );
  },

  // --------------------------------------------------------------------------
  // Sync Sessions, Item Changes & Mappings (`/ms-sync/*`)
  // --------------------------------------------------------------------------
  listPipeSyncSessions(pipeId: UUID, limit = 20): Promise<SyncSessionSummaryResponse[]> {
    return apiRequest<SyncSessionSummaryResponse[]>('/ms-sync/last-sync-sessions', {
      query: { pipe_id: pipeId, limit },
    });
  },

  searchSyncSessions(
    page = 0,
    pageSize = 25,
  ): Promise<{ data: SyncSession[]; total: number; page: number; page_size: number }> {
    return apiRequest<{ data: SyncSession[]; total: number; page: number; page_size: number }>(
      '/ms-sync/sync-processes/search',
      {
        method: 'POST',
        body: { page, page_size: pageSize },
      },
    );
  },

  getSyncSessionDetail(sessionId: UUID): Promise<SyncSessionDetailResponse> {
    return apiRequest<SyncSessionDetailResponse>(`/ms-sync/sync-processes/${sessionId}`);
  },

  getItemChangesBySession(sessionId: UUID): Promise<ItemChangeRecord[]> {
    return apiRequest<ItemChangeRecord[]>(`/ms-sync/item-changes-by-sync-session-id/${sessionId}`);
  },

  getPipeSyncedItems(pipeId: UUID): Promise<SyncItemMapping[]> {
    return apiRequest<SyncItemMapping[]>(`/ms-sync/items/pipe/${pipeId}`);
  },

  getPipeSyncedTasks(pipeId: UUID): Promise<SyncItemMapping[]> {
    return apiRequest<SyncItemMapping[]>(`/ms-sync/tasks/pipe/${pipeId}`);
  },

  getPipeSyncedAssignees(pipeId: UUID): Promise<SyncItemMapping[]> {
    return apiRequest<SyncItemMapping[]>(`/ms-sync/assignees/pipe/${pipeId}`);
  },

  getPipeSyncedResources(pipeId: UUID): Promise<SyncItemMapping[]> {
    return apiRequest<SyncItemMapping[]>(`/ms-sync/resources/pipe/${pipeId}`);
  },

  getPipeSyncedItemHistory(pipeId: UUID): Promise<SyncedItemHistoryRecord[]> {
    return apiRequest<SyncedItemHistoryRecord[]>(`/ms-sync/synced-item-history/pipe/${pipeId}`);
  },

  validateGanttTasks(
    tasks: Array<{
      id: string;
      name: string;
      parent_id?: string | null;
      predecessors?: string[];
      start_date?: string | null;
      end_date?: string | null;
      duration_days?: number | null;
      is_milestone?: boolean;
    }>,
  ): Promise<GanttValidationResult> {
    return apiRequest<GanttValidationResult>('/ms-sync/tasks/validate', {
      method: 'POST',
      body: { tasks },
    });
  },

  cleanupDuplicateTasks(pipeId: UUID): Promise<DuplicateCleanupResult> {
    return apiRequest<DuplicateCleanupResult>('/ms-sync/tasks/cleanup-duplicates', {
      method: 'POST',
      body: { pipe_id: pipeId },
    });
  },

  releasePipeLock(pipeId: UUID): Promise<{ pipe_id: UUID; released: boolean }> {
    return apiRequest<{ pipe_id: UUID; released: boolean }>(
      `/ms-sync/synchronization-locks/pipe/${pipeId}`,
      { method: 'DELETE' },
    );
  },

  // --------------------------------------------------------------------------
  // Queue Monitoring & Control (`/ms-queue/*`)
  // --------------------------------------------------------------------------
  getQueueCounts(): Promise<QueueCountsResponse> {
    return apiRequest<QueueCountsResponse>('/ms-queue/job/counts');
  },

  getQueueStatus(): Promise<QueueStatusResponse> {
    return apiRequest<QueueStatusResponse>('/ms-queue/job/queue/status');
  },

  enqueuePipeSyncJob(pipeId: UUID, direction?: SyncDirection): Promise<TriggerSyncJobResponse> {
    return apiRequest<TriggerSyncJobResponse>(`/ms-queue/job/sync/pipe/${pipeId}`, {
      method: 'POST',
      body: direction ? { direction } : {},
    });
  },

  pauseQueue(): Promise<MaintenanceStatusResponse> {
    return apiRequest<MaintenanceStatusResponse>('/ms-queue/job/pause', {
      method: 'POST',
      scope: 'admin',
    });
  },

  resumeQueue(): Promise<MaintenanceStatusResponse> {
    return apiRequest<MaintenanceStatusResponse>('/ms-queue/job/resume', {
      method: 'POST',
      scope: 'admin',
    });
  },

  cleanQueue(): Promise<{ removed_jobs: number }> {
    return apiRequest<{ removed_jobs: number }>('/ms-queue/job/clean', {
      method: 'POST',
      scope: 'admin',
    });
  },

  // --------------------------------------------------------------------------
  // Main Orchestrator: Procore, Smartsheet, System Settings & Maintenance (`/ms-main/*`)
  // --------------------------------------------------------------------------
  getServicesCatalog(): Promise<ServiceCatalogEntry[]> {
    return apiRequest<ServiceCatalogEntry[]>('/ms-main/services');
  },

  async listProcoreProjects(companyId?: number): Promise<ProcoreRemoteProject[]> {
    const rows = await apiRequest<
      Array<ProcoreRemoteProject & { procore_project_id?: number }>
    >('/ms-main/procore/projects', {
      query: companyId ? { company_id: companyId } : undefined,
    });
    if (!Array.isArray(rows)) return [];
    return rows.map((r) => ({
      ...r,
      id: Number(r.id ?? r.procore_project_id ?? 0),
    }));
  },

  async listSmartsheetSheets(): Promise<SmartsheetSheetSummary[]> {
    const rows = await apiRequest<
      Array<SmartsheetSheetSummary & { sheet_id?: number }>
    >('/ms-main/smartsheet/sheets');
    if (!Array.isArray(rows)) return [];
    return rows.map((s) => ({
      ...s,
      id: Number(s.id ?? s.sheet_id ?? 0),
    }));
  },

  async createSmartsheetSheet(
    name: string,
    columns?: SmartsheetRemoteColumn[],
  ): Promise<SmartsheetRemoteSheet> {
    const sheet = await apiRequest<SmartsheetRemoteSheet & { sheet_id?: number }>(
      '/ms-main/smartsheet/sheets',
      {
        method: 'POST',
        body: columns ? { name, columns } : { name },
      },
    );
    return {
      ...sheet,
      id: Number(sheet.id ?? sheet.sheet_id ?? 0),
    };
  },

  async getSmartsheetSheet(sheetId: number): Promise<SmartsheetRemoteSheet> {
    const sheet = await apiRequest<SmartsheetRemoteSheet & { sheet_id?: number }>(
      `/ms-main/smartsheet/sheets/${sheetId}`,
    );
    return {
      ...sheet,
      id: Number(sheet.id ?? sheet.sheet_id ?? 0),
    };
  },

  listSmartsheetContacts(): Promise<SmartsheetContact[]> {
    return apiRequest<SmartsheetContact[]>('/ms-main/smartsheet/contacts');
  },

  registerSmartsheetWebhook(
    sheetId: number,
    callbackUrl: string,
    name?: string,
  ): Promise<SmartsheetWebhookInfo> {
    return apiRequest<SmartsheetWebhookInfo>('/ms-main/smartsheet/webhook', {
      method: 'POST',
      body: {
        sheet_id: sheetId,
        callback_url: callbackUrl,
        name,
      },
    });
  },

  deleteSmartsheetWebhook(webhookId: number): Promise<{ deleted: boolean; webhook_id: number }> {
    return apiRequest<{ deleted: boolean; webhook_id: number }>(
      `/ms-main/smartsheet/webhook/${webhookId}`,
      { method: 'DELETE' },
    );
  },

  syncPipeMain(pipeId: UUID, direction?: SyncDirection): Promise<TriggerSyncJobResponse> {
    return apiRequest<TriggerSyncJobResponse>(`/ms-main/sync/pipe/${pipeId}`, {
      method: 'POST',
      body: direction ? { direction } : {},
    });
  },

  syncProjectPipes(
    projectId: UUID,
    direction?: SyncDirection,
  ): Promise<{ project_id: UUID; job_id: string; direction: SyncDirection; status: string }> {
    return apiRequest<{ project_id: UUID; job_id: string; direction: SyncDirection; status: string }>(
      `/ms-main/sync/project/${projectId}`,
      {
        method: 'POST',
        body: direction ? { direction } : {},
      },
    );
  },

  getMaintenanceStatus(): Promise<MaintenanceStatusResponse> {
    return apiRequest<MaintenanceStatusResponse>('/ms-main/get-setting-info');
  },

  setMaintenanceMode(maintenanceMode: boolean): Promise<MaintenanceStatusResponse> {
    const endpoint = maintenanceMode ? '/ms-main/maintenance/on' : '/ms-main/maintenance/off';
    return apiRequest<MaintenanceStatusResponse>(endpoint, {
      method: 'POST',
      scope: 'admin',
    });
  },

  listSystemSettings(): Promise<SystemSettingRecord[]> {
    return apiRequest<SystemSettingRecord[]>('/ms-main/system-settings', {
      scope: 'admin',
    });
  },

  upsertSystemSetting(
    key: string,
    value: Record<string, unknown>,
    maintenanceMode = false,
  ): Promise<SystemSettingRecord> {
    return apiRequest<SystemSettingRecord>('/ms-main/settings', {
      method: 'PUT',
      body: {
        key,
        value,
        maintenance_mode: maintenanceMode,
      },
      scope: 'admin',
    });
  },
};
